import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { BuilderProvider, useBuilder } from './context/BuilderContext'
import Toolbox from './components/Toolbox'
import Canvas from './components/Canvas'
import SettingsPanel from './components/SettingsPanel'
import TopBar from './components/TopBar'
import AIGenerationModal from './components/AIGenerationModal'
import TemplatePickerModal from './components/TemplatePickerModal'
import VersionHistoryModal from './components/VersionHistoryModal'
import { createBlock } from './utils/blockDefaults'
import axios from '../../lib/axios'
import '../Pages.css'

function BuilderContent({ templateId }) {
  const { blocks, setBlocks, setSubject, addBlock, loadTemplate, updateBodySettings, bodySettings } = useBuilder()
  const [loading, setLoading] = useState(true)
  const [activeId, setActiveId] = useState(null)
  const [showAIModal, setShowAIModal] = useState(false)
  const [showTemplatePicker, setShowTemplatePicker] = useState(false)
  const [previewMode, setPreviewMode] = useState('desktop')
  const [toolboxCollapsed, setToolboxCollapsed] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  )

  useEffect(() => {
    if (!templateId) {
      setLoading(false)
      return
    }

    axios
      .get(`master-templates/${templateId}`)
      .then((res) => {
        const data = res.data

        let templateData = null
        if (data.html_json) {
          if (typeof data.html_json === 'string') {
            try {
              templateData = JSON.parse(data.html_json)
            } catch (e) {
              console.error('Invalid html_json', e)
            }
          } else {
            templateData = data.html_json
          }
        }

        if (templateData && templateData.blocks) {
          setBlocks(templateData.blocks || [])
          setSubject(templateData.subject || '')
          if (templateData.bodySettings) {
            updateBodySettings(templateData.bodySettings)
          }
        } else {
          setBlocks([])
          setSubject(data.subject || '')
          setShowTemplatePicker(true)
        }
      })
      .catch((err) => {
        console.error(err)
        alert('Failed to load template')
      })
      .finally(() => setLoading(false))
  }, [templateId])

  const handleDragStart = (event) => {
    setActiveId(event.active.id)
  }

  const handleSelectTemplate = (templateData) => {
    loadTemplate(templateData)
  }

  const handleTogglePreview = (mode) => {
    setPreviewMode(mode)
  }

  const handleAIGenerate = async (prompt) => {
    try {
      const response = await axios.post('/ai_prompt/generate-text/', {
        ai_prompt: prompt,
        conversation_history: []
      })

      const { subject: generatedSubject, blocks: generatedBlocks, explanation } = response.data

      if (generatedSubject) {
        setSubject(generatedSubject)
      }

      if (generatedBlocks && Array.isArray(generatedBlocks) && generatedBlocks.length > 0) {
        const processAIBlock = (aiBlock) => {
          const block = createBlock(aiBlock.type, bodySettings)
          if (aiBlock.settings) {
            block.settings = {
              ...block.settings,
              ...aiBlock.settings
            }

            // Process nested blocks inside columns — AI may return content as object or blocks array
            if (aiBlock.type === 'columns' && Array.isArray(aiBlock.settings.columns)) {
              block.settings.columns = aiBlock.settings.columns.map((col, idx) => {
                // Collect nested blocks from both col.blocks and col.content (AI sometimes puts block in content)
                const rawBlocks = [...(col.blocks || [])]
                if (col.content && typeof col.content === 'object' && col.content.type) {
                  rawBlocks.push(col.content)
                }

                return {
                  id: col.id || `col-${idx}`,
                  content: (typeof col.content === 'string') ? col.content : '',
                  blocks: rawBlocks.map(nestedBlock => {
                    const processed = createBlock(nestedBlock.type, bodySettings)
                    if (nestedBlock.settings) {
                      processed.settings = {
                        ...processed.settings,
                        ...nestedBlock.settings
                      }
                    }
                    return processed
                  })
                }
              })
              // Ensure columnCount matches actual columns length
              block.settings.columnCount = block.settings.columns.length
            }
          }
          return block
        }

        const builderBlocks = generatedBlocks.map(processAIBlock)

        setBlocks(builderBlocks)
      } else {
        throw new Error('No blocks received from AI')
      }

      if (explanation) {
        console.log('AI Generation Explanation:', explanation)
      }

      alert(`Email generated successfully!\n\nCreated ${generatedBlocks.length} components that you can now edit, drag, and customize.`)
    } catch (error) {
      console.error('AI Generation error:', error)
      const errData = error.response?.data
      if (errData?.error === 'LIMIT_EXCEEDED' && errData?.resource === 'ai_credits') {
        // Handled by the global AICreditsModal (see src/lib/axios.js interceptor);
        // don't surface an inline error here — avoids a duplicate/misleading message.
        return
      }
      if (errData?.error === 'LIMIT_EXCEEDED') {
        throw new Error(`You've used ${errData.used} of ${errData.limit} AI email credits. Please upgrade your plan to continue.`)
      }
      throw new Error(errData?.detail || 'Failed to generate email content')
    }
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    setActiveId(null)

    if (!over) return

    if (active.id.toString().startsWith('toolbox-')) {
      const blockType = active.data.current?.type
      if (blockType) {
        const newBlock = createBlock(blockType, bodySettings)

        if (over.id.toString().includes('-col-')) {
          const parts = over.id.split('-col-')
          const parentBlockId = parts[0]
          const colIdx = parseInt(parts[1])

          setBlocks(prev => {
            const updated = prev.map(block => {
              if (block.id === parentBlockId && block.type === 'columns') {
                const updatedColumns = JSON.parse(JSON.stringify(block.settings.columns || []))

                while (updatedColumns.length <= colIdx) {
                  updatedColumns.push({ blocks: [], content: '' })
                }

                if (!updatedColumns[colIdx]) {
                  updatedColumns[colIdx] = { blocks: [], content: '' }
                }

                if (!updatedColumns[colIdx].blocks) {
                  updatedColumns[colIdx].blocks = []
                }

                updatedColumns[colIdx] = {
                  ...updatedColumns[colIdx],
                  blocks: [...updatedColumns[colIdx].blocks, newBlock]
                }

                return {
                  ...block,
                  settings: {
                    ...block.settings,
                    columns: updatedColumns
                  }
                }
              }
              return block
            })

            return updated
          })
          return
        }

        if (over.id === 'canvas-droppable') {
          addBlock(newBlock)
        } else {
          const overIndex = blocks.findIndex(b => b.id === over.id)
          if (overIndex !== -1) {
            const isLastBlock = overIndex === blocks.length - 1

            if (isLastBlock) {
              addBlock(newBlock)
            } else {
              const newBlocks = [...blocks]
              newBlocks.splice(overIndex, 0, newBlock)
              setBlocks(newBlocks)
            }
          } else {
            addBlock(newBlock)
          }
        }
      }
      return
    }

    if (active.id === over.id) return

    const oldIndex = blocks.findIndex(b => b.id === active.id)
    const newIndex = blocks.findIndex(b => b.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      setBlocks(arrayMove(blocks, oldIndex, newIndex))
    }
  }

  if (loading) {
    return (
      <div className="page center">
        <p>Loading builder...</p>
      </div>
    )
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <TopBar
        templateId={templateId}
        onOpenAIModal={() => setShowAIModal(true)}
        onOpenTemplatePicker={() => setShowTemplatePicker(true)}
        previewMode={previewMode}
        onTogglePreview={handleTogglePreview}
        toolboxCollapsed={toolboxCollapsed}
        onToggleToolbox={() => setToolboxCollapsed(prev => !prev)}
        onOpenVersionHistory={() => setShowVersionHistory(true)}
      />

      <AIGenerationModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onGenerate={handleAIGenerate}
      />

      <TemplatePickerModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      <VersionHistoryModal
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        templateId={templateId}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Toolbox collapsed={toolboxCollapsed} />

          <SortableContext
            items={blocks.map(b => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <Canvas previewMode={previewMode} />
          </SortableContext>

          <SettingsPanel />
        </div>

        <DragOverlay>
          {activeId ? (
            <div style={{
              padding: '1.25rem 1.5rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: 'grabbing',
              minWidth: '180px',
              textAlign: 'center',
              border: '2px solid rgba(255,255,255,0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {activeId.toString().startsWith('toolbox-')
                ? activeId.replace('toolbox-', '')
                : 'Moving Block'}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}

export default function CustomEmailBuilder() {
  const { id: templateId } = useParams()

  return (
    <BuilderProvider>
      <BuilderContent templateId={templateId} />
    </BuilderProvider>
  )
}
